import { DocumentUser } from 'apis/entities/document.entity'
import SidebarLayout from 'components/SidebarLayout'
import { useApi } from 'providers/ApiProvider'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { StyleUtil } from 'utils/StyleUtil'
import { getAlternatingStyle } from 'utils/TableUtils'
import { ToastUtil } from 'utils/ToastUtil'
import DropDownMenu, { DropDownItem } from 'views/DropDownMenu'
import SearchInput from 'views/SearchInput'
import Spinner from 'views/Spinner'
import copySvg from 'images/copy.svg'
import sortSvg from 'images/sort.svg'
import sort2Svg from 'images/sort2.svg'
import { Tooltip } from 'react-tooltip'

import { ReactComponent as FcImagesSvg } from 'images/file-catalogue-images.svg'
import { ReactComponent as FcDocumentsSvg } from 'images/file-catalogue-documents.svg'
import { ReactComponent as FcPresentationsSvg } from 'images/file-catalogue-presentations.svg'
import { ReactComponent as FcSpreadsheetsSvg } from 'images/file-catalogue-spreadsheets.svg'
import { ReactComponent as FcAudioSvg } from 'images/file-catalogue-audios.svg'
import { ReactComponent as FcVideoSvg } from 'images/file-catalogue-videos.svg'
import { ReactComponent as FcOtherSvg } from 'images/file-catalogue-others.svg'

import FilePreview from '../views/FilePreview'
import { ButtonCopy } from 'components/ButtonCopy'
import { getExtensionIcon } from '../utils/FileUtil'
import { LxFile } from '../apis/entities/lx-file.entity'
import { getDateText, getTimeText } from 'utils/StringUtil'
import ImagesSection from './ImagesSection'
import menuRight from '../images/menu-right-alt.svg'
import menuLeft from '../images/menu-left-alt.svg'
import close2 from '../images/close2.svg'
import hasNotesSvg from '../images/has-notes.svg'
import noNotesSvg from '../images/no-notes.svg'
import ButtonSpinner from 'views/ButtonSpinner'
import RightArrowSvg from 'components/RightArrowSvg'
import { Role, useCompositeAuth } from 'providers/CompositeAuthProvider'

const FileErrorMessage = `
  <div style="
      font-family: 'Roboto Mono', monospace; 
      font-weight: 500;
      width: 380px;
      font-size: 14px;
      padding-top: 2px;
      padding-bottom: 2px;">
    File catalogue does not support image upload.
    Please search the image catalogue or make a design request if you need a new asset.
  </div>
  <style>
    .Toastify__toast::before {
        top: 20px !important;
    }
    .Toastify__toast > button > svg {
      margin-top: 10px !important;
    }
  </style>
`

enum SortBy {
  PublishedBy = 'publishedUserName',
  DateTime = 'createdAt',
  Filename = 'name',
  BusinessUnit = 'business_unit',
  Duration = 'duration',
}

enum Section {
  None = 0,
  Search = 1,
  Upload = 2,
  ManageSection = 3,
  ImagesSection = 4,
}

type Direction = 'ascending' | 'descending' | ''

const publishedByOptionLabel = {
  id: '0',
  name: 'Published by',
  value: '0',
  isLabel: true,
}

const publishedByOptionShowAll = {
  id: '1',
  name: 'All authors',
  value: '',
  isLabel: false,
}

const tooltipStyle = {
  backgroundColor: '#fff',
  color: '#000',
  borderRadius: '6px',
}

const tooltipStyleReverse = {
  backgroundColor: '#6b69c1',
  color: '#fff',
  borderRadius: '6px',
}
interface UploadedLxFile {
  url: string
  filename: string
}
interface LxFileContainer {
  objectURL: string
  filenameOverride: string
  file: File
}

const NotesModal = ({
  isOpen,
  onClose,
  initialNotes,
  onSave,
}: {
  isOpen: boolean
  onClose: () => void
  initialNotes: string
  onSave: (notes: string) => void
}) => {
  const [notes, setNotes] = useState(initialNotes)

  const handleSave = () => {
    onSave(notes)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(255,255,255,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: '24px',
          boxShadow: `0px 381px 107px 0px rgba(0, 0, 0, 0.00), 
            0px 244px 98px 0px rgba(0, 0, 0, 0.01), 
            0px 137px 82px 0px rgba(0, 0, 0, 0.05), 
            0px 61px 61px 0px rgba(0, 0, 0, 0.09), 
            0px 15px 34px 0px rgba(0, 0, 0, 0.10)`,
        }}
      >
        <div
          style={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-end',
            paddingBottom: 24,
          }}
          onClick={onClose}
        >
          <img src={close2} alt="Close" style={{ height: 24 }} />
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{
            width: '500px',
            height: '96px',
            border: '1px solid #D5D5DB',
            paddingLeft: 12,
            paddingTop: 6,
            color: '#92929D',
            fontSize: '14px',
            fontFamily: 'Roboto',
            fontWeight: 400,
            lineHeight: '20px',
            fontStyle: 'normal',
            outline: 'none',
          }}
          onKeyDown={async (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSave()
            }
          }}
          placeholder="Type notes here"
        />
        <div
          style={{
            marginTop: 18,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <button
            data-testid="btn-save-notes"
            className={StyleUtil.buttonPrimaryUnsw}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

// single source for business units used by upload, filter and change modal.
// 'OTHER' is stored as null in the database
const businessUnitOptions = [
  { value: 'AGSM', label: 'AGSM', uploadStyle: StyleUtil.buttonPurple },
  {
    value: 'UNSWO',
    label: 'UNSW Online',
    uploadStyle: StyleUtil.buttonPrimaryUnsw,
  },
  { value: 'OTHER', label: 'Other', uploadStyle: StyleUtil.buttonBlackWhite },
]

const toBusinessUnitOption = (businessUnit?: string | null): string =>
  businessUnit || 'OTHER'

export const BusinessUnitModal = ({
  initialBusinessUnit,
  isSaving,
  onClose,
  onSave,
}: {
  initialBusinessUnit: string
  isSaving: boolean
  onClose: () => void
  onSave: (businessUnit: string) => void
}) => {
  const [businessUnit, setBusinessUnit] = useState(initialBusinessUnit)

  return (
    <div
      data-testid="business-unit-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(255,255,255,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: 461,
          backgroundColor: '#fff',
          padding: '12px 24px 24px',
          boxShadow: `0px 381px 107px 0px rgba(0, 0, 0, 0.00),
            0px 244px 98px 0px rgba(0, 0, 0, 0.01),
            0px 137px 82px 0px rgba(0, 0, 0, 0.05),
            0px 61px 61px 0px rgba(0, 0, 0, 0.09),
            0px 15px 34px 0px rgba(0, 0, 0, 0.10)`,
        }}
      >
        <div className="flex justify-end">
          <img
            data-testid="btn-close-business-unit"
            className="cursor-pointer"
            src={close2}
            alt="Close"
            style={{ height: 20 }}
            onClick={onClose}
          />
        </div>
        <h3 className="mt-3 text-[16px] font-bold">
          Choose the business unit you’d like to assign to
        </h3>
        <div className="mt-3 flex flex-col gap-3">
          {businessUnitOptions.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2 text-[14px]"
            >
              <input
                type="radio"
                name="businessUnit"
                value={option.value}
                checked={businessUnit === option.value}
                onChange={() => setBusinessUnit(option.value)}
                data-testid={`radio-business-unit-${option.value}`}
              />
              {option.label}
            </label>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-6">
          <button
            data-testid="btn-cancel-business-unit"
            className={StyleUtil.buttonSecondaryUnsw}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            data-testid="btn-save-business-unit"
            className={`${StyleUtil.buttonPrimaryUnsw} w-[66px]`}
            disabled={isSaving || !businessUnit}
            onClick={() => onSave(businessUnit)}
          >
            {isSaving ? <ButtonSpinner /> : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0)
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

export function SearchSection(props: any) {
  const colStyle = getAlternatingStyle()
  const [editingDocs, setEditingDocs] = useState<LxFile[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [documents, setDocuments] = useState<LxFile[]>([])
  const refPublishedBy = useRef<string>('')
  const refBusinessUnit = useRef<string>('')
  const refDurationFilter = useRef<string>('')
  const [sortConfig, setSortConfig] = useState<{
    key: SortBy
    direction: Direction
  } | null>({ key: SortBy.DateTime, direction: 'descending' })
  const [publishedByOptions, setPublishedByOptions] = useState<any>([
    publishedByOptionLabel,
  ])
  const [isClickDelete, setIsClickDelete] = useState<boolean>(false)
  const refSearchTextInput = useRef<string>('')
  const refStartDate = useRef<Date | undefined>(undefined)
  const [isFetchedPublishedBy, setIsFetchedPublishedBy] =
    useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [checkedIds, setCheckedIds] = useState<string[]>([])
  const [orderByFilename, setOrderByFilename] = useState<Direction>('')
  const [orderByPublishedBy, setOrderByPublishedBy] = useState<Direction>('')
  const [orderByDateTime, setOrderByDateTime] =
    useState<Direction>('descending')
  const [orderByBusinessUnit, setOrderByBusinessUnit] = useState<Direction>('')
  const [orderByDuration, setOrderByDuration] = useState<Direction>('')
  const [isChangingBusinessUnit, setIsChangingBusinessUnit] =
    useState<boolean>(false)
  const [isSavingBusinessUnit, setIsSavingBusinessUnit] =
    useState<boolean>(false)
  const { fileTypeFilter, isManagement } = props
  const {
    getLxFiles,
    getLxFilePublishedBy,
    deleteLxFile,
    updateLxFile,
    generateLxFileEmbedding,
  } = useApi()
  const { isLogged, getRole } = useCompositeAuth()
  const role = getRole()
  const isSuperAdmin = role === Role.SuperAdmin
  const isAdmin = role === Role.Admin
  const canDeleteFromCatalogue = isSuperAdmin
  const canEditCatalog = isSuperAdmin || isAdmin

  const searchDocuments = async () => {
    if (!isLogged) return
    const filter = `published=1&publishedBy=${
      refPublishedBy.current
    }&fileTypeFilter=${JSON.stringify(fileTypeFilter)}&businessUnit=${
      refBusinessUnit.current
    }&durationFilter=${refDurationFilter.current}`
    const search = refSearchTextInput.current
    setIsSearching(true)
    const docs = await getLxFiles(filter, search, refStartDate.current)
    if (docs) {
      setDocuments(docs)
    }
    setIsSearching(false)
  }

  useEffect(() => {
    if (!isLogged) return
    getLxFilePublishedBy().then((users) => {
      let allOptions = [publishedByOptionLabel]
      if (Array.isArray(users)) {
        let options = users.map((u: DocumentUser, _index: number) => {
          return {
            id: u.publishedUserId,
            name: u.publishedUserName,
            value: u.publishedUserId,
            isLabel: false,
          }
        })
        allOptions = allOptions.concat(options)
        allOptions.push(publishedByOptionShowAll)
        setPublishedByOptions(allOptions)
        setIsFetchedPublishedBy(true)
      }
    })
  }, [getLxFilePublishedBy, isLogged])

  useEffect(() => {
    if (!isLogged) return
    refDurationFilter.current = ''
    const filter = `published=1&publishedBy=${
      refPublishedBy.current
    }&fileTypeFilter=${JSON.stringify(fileTypeFilter)}&businessUnit=${
      refBusinessUnit.current
    }&durationFilter=${refDurationFilter.current}`
    const search = refSearchTextInput.current
    setIsSearching(true)

    getLxFiles(filter, search, refStartDate.current).then((docs) => {
      if (docs) {
        setDocuments(docs)
      }
      setIsSearching(false)
    })
  }, [fileTypeFilter, isLogged, getLxFiles])

  const getSortingText = (key: SortBy): string => {
    switch (key) {
      case SortBy.Filename:
        if (orderByFilename === 'ascending') return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.PublishedBy:
        if (orderByPublishedBy === 'ascending') return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.DateTime:
        if (orderByDateTime === 'ascending') return 'Sort new to old'
        return 'Sort old to new'
      case SortBy.BusinessUnit:
        if (orderByBusinessUnit === 'ascending') return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.Duration:
        if (orderByDuration === 'ascending') return 'Sort long to short'
        return 'Sort short to long'
      default:
        return ''
    }
  }

  const sortedDocuments = useMemo(() => {
    let sortableItems = documents?.length ? [...documents] : []
    if (sortConfig !== null) {
      sortableItems.sort((a: LxFile, b: LxFile) => {
        const rawA = a[sortConfig.key]
        const rawB = b[sortConfig.key]
        const valA = typeof rawA === 'string' ? rawA.toLowerCase() : rawA ?? ''
        const valB = typeof rawB === 'string' ? rawB.toLowerCase() : rawB ?? ''

        if (!valA && !valB) return 0
        if (!valA) return 1
        if (!valB) return -1

        if (valA < valB) {
          return sortConfig.direction === 'ascending' ? -1 : 1
        }
        if (valA > valB) {
          return sortConfig.direction === 'ascending' ? 1 : -1
        }
        return 0
      })
    }
    return sortableItems
  }, [documents, sortConfig])

  const onInputChangedSearch = (value: string) => {
    refSearchTextInput.current = value
  }

  const onEnterSearch = async () => {
    setSortConfig(null)
    setOrderByFilename('')
    setOrderByPublishedBy('')
    setOrderByDateTime('')
    setOrderByBusinessUnit('')
    setOrderByDuration('')
    await searchDocuments()
  }

  const onSelectDate = async (item: DropDownItem) => {
    const value = item.value
    if (value === undefined) {
      // show all
      refStartDate.current = undefined
    } else if (value > 0) {
      const startDate = new Date()
      // caculate start date by value which is number of days
      startDate.setDate(startDate.getDate() - value)
      refStartDate.current = startDate
    }
    await searchDocuments()
  }

  const onSelectPublishedBy = async (item: DropDownItem) => {
    refPublishedBy.current = item.value
    await searchDocuments()
  }

  const onSelectBusinessUnit = async (item: DropDownItem) => {
    refBusinessUnit.current = item.value
    await searchDocuments()
  }

  const onSelectDuration = async (item: DropDownItem) => {
    refDurationFilter.current = item.value
    await searchDocuments()
  }

  const onClickDelete = () => {
    if (checkedIds.length === 0) {
      ToastUtil.warning('Please select at least one file')
    } else {
      setIsClickDelete(true)
    }
  }

  const onChangeCheckbox = (checked: boolean, id: string) => {
    setIsClickDelete(false)
    if (checked) {
      setCheckedIds([...checkedIds, id])
      // If all checkboxes are checked
      if (checkedIds.length === documents.length - 1) {
        setSelectAll(true)
      }
    } else {
      setCheckedIds(checkedIds.filter((y) => y !== id))
      // If any checkbox is unchecked
      setSelectAll(false)
    }
  }

  const onConfirmDelete = async () => {
    setIsLoading(true)

    for (const x of checkedIds) {
      await deleteLxFile(x)
    }
    await searchDocuments()
    setIsLoading(false)
    ToastUtil.success(`Deleted successfully`)

    document.querySelectorAll('[data-id="checkbox"]').forEach((x: any) => {
      x.checked = false
    })
    setCheckedIds([])
    setIsClickDelete(false)
  }

  const onClickChangeBusinessUnit = () => {
    if (checkedIds.length === 0) {
      ToastUtil.warning('Please select at least one file')
    } else {
      setIsChangingBusinessUnit(true)
    }
  }

  // pre-select the business unit only when every selected file shares it
  const getInitialBusinessUnit = (): string => {
    const units = new Set(
      documents
        .filter((doc) => checkedIds.includes(doc.id))
        .map((doc) => toBusinessUnitOption(doc.business_unit)),
    )
    return units.size === 1 ? [...units][0] : ''
  }

  const onSaveBusinessUnit = async (businessUnit: string) => {
    setIsSavingBusinessUnit(true)
    try {
      for (const id of checkedIds) {
        await updateLxFile(id, {
          business_unit: businessUnit === 'OTHER' ? null : businessUnit,
        })
      }
      ToastUtil.success('Changed business unit successfully')
      setIsChangingBusinessUnit(false)
      setCheckedIds([])
      setSelectAll(false)
      // re-fetch, files may no longer match the current business unit filter
      await searchDocuments()
    } catch (err) {
      console.error(err)
      ToastUtil.error('Change business unit failed')
    } finally {
      setIsSavingBusinessUnit(false)
    }
  }

  const onClickCopy = async (lxFile: LxFile) => {
    navigator.clipboard.writeText(lxFile.fileUrl)
    ToastUtil.success('URL link copied to clipboard')
  }

  const onClickSort = (key: SortBy) => {
    let ascending = true
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      ascending = false
    }

    switch (key) {
      case SortBy.Filename:
        setOrderByFilename(ascending ? 'ascending' : 'descending')
        break
      case SortBy.PublishedBy:
        setOrderByPublishedBy(ascending ? 'ascending' : 'descending')
        break
      case SortBy.DateTime:
        setOrderByDateTime(ascending ? 'ascending' : 'descending')
        break
      case SortBy.BusinessUnit:
        setOrderByBusinessUnit(ascending ? 'ascending' : 'descending')
        break
      case SortBy.Duration:
        setOrderByDuration(ascending ? 'ascending' : 'descending')
        break
      default:
        break
    }

    setSortConfig({ key, direction: ascending ? 'ascending' : 'descending' })
  }

  const getTooptipStyle = (key: SortBy): any => {
    switch (key) {
      case SortBy.Filename:
        return orderByFilename === 'ascending'
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.PublishedBy:
        return orderByPublishedBy === 'ascending'
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.DateTime:
        return orderByDateTime === 'ascending'
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.BusinessUnit:
        return orderByBusinessUnit === 'ascending'
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.Duration:
        return orderByDuration === 'ascending'
          ? tooltipStyleReverse
          : tooltipStyle
      default:
        return tooltipStyle
    }
  }

  const saveNotes = useCallback(
    async (notes: string) => {
      if (!editingDocs.length) return

      for (const doc of editingDocs) {
        await updateLxFile(doc.id, {
          comments: notes,
        })
      }
      setDocuments((currentDocuments) =>
        currentDocuments.map((doc) =>
          editingDocs.some((editingDoc) => editingDoc.id === doc.id)
            ? {
                ...doc,
                comments: notes,
              }
            : doc,
        ),
      )
      ToastUtil.success(`Changed successfully`)
    },
    [editingDocs, updateLxFile, setDocuments],
  )

  return (
    <>
      {editingDocs.length ? (
        <NotesModal
          isOpen={editingDocs.length > 0}
          onClose={() => {
            setEditingDocs([])
          }}
          initialNotes={editingDocs.length === 1 ? editingDocs[0].comments : ''}
          onSave={saveNotes}
        />
      ) : null}
      {isChangingBusinessUnit && (
        <BusinessUnitModal
          initialBusinessUnit={getInitialBusinessUnit()}
          isSaving={isSavingBusinessUnit}
          onClose={() => setIsChangingBusinessUnit(false)}
          onSave={onSaveBusinessUnit}
        />
      )}
      {!isFetchedPublishedBy && <Spinner />}
      {isFetchedPublishedBy && (
        <div className="flex h-[100%] flex-col pb-10 pt-8" id="THE_PARENT">
          <div id="THE_FILTERS" className="flex items-start justify-between">
            <div className="w-[456px]">
              <SearchInput
                onInputChanged={onInputChangedSearch}
                onEnter={onEnterSearch}
                isSearching={isSearching}
              />
            </div>

            <div className="flex justify-between gap-3">
              <DropDownMenu
                items={[
                  {
                    id: '0',
                    name: 'Date',
                    value: undefined,
                    isLabel: true,
                  },
                  { id: '1', name: 'Last 7 days', value: 7 },
                  { id: '2', name: 'Last 30 days', value: 30 },
                  { id: '3', name: 'Last 6 months', value: 183 },
                  {
                    id: '4',
                    name: 'All dates',
                    value: undefined,
                    default: true,
                  },
                ]}
                onSelected={onSelectDate}
              />
              <DropDownMenu
                items={publishedByOptions}
                onSelected={onSelectPublishedBy}
              />
              <DropDownMenu
                items={[
                  {
                    id: '0',
                    name: 'Business unit',
                    value: '',
                    isLabel: true,
                  },
                  { id: '1', name: 'All files', value: '', default: true },
                  // filtering by 'OTHER' isn't supported here yet
                  ...businessUnitOptions
                    .filter((option) => option.value !== 'OTHER')
                    .map((option, i) => ({
                      id: String(i + 2),
                      name: option.label,
                      value: option.value,
                    })),
                ]}
                onSelected={onSelectBusinessUnit}
              />
              {(fileTypeFilter.audio || fileTypeFilter.videos) && (
                <DropDownMenu
                  key={
                    fileTypeFilter.audio ? 'duration-audio' : 'duration-videos'
                  }
                  items={[
                    {
                      id: '0',
                      name: 'Duration',
                      value: '',
                      isLabel: true,
                    },
                    {
                      id: '1',
                      name: 'All durations',
                      value: '',
                      default: true,
                    },
                    { id: '2', name: 'Under 15 min', value: '0-15' },
                    { id: '3', name: 'Over 15 min', value: '15plus' },
                  ]}
                  onSelected={onSelectDuration}
                />
              )}
            </div>
          </div>
          <div
            id="THE_SUBFILTERS"
            className={`flex items-center justify-start pt-2 ${
              canEditCatalog ? 'pb-6' : 'pb-2'
            }`}
          >
            {!isSearching && documents.length > 0 && (
              <>
                {canDeleteFromCatalogue && isClickDelete && (
                  <>
                    <span className="mr-3 text-[14px]">Are you sure?</span>
                    <button
                      data-testid="btn-delete-from-catalogue-yes"
                      onClick={onConfirmDelete}
                      className={`${StyleUtil.buttonPrimaryUnsw} w-[50px]`}
                    >
                      {isLoading ? <ButtonSpinner /> : 'Yes'}
                    </button>
                    <span
                      data-testid="btn-delete-from-catalogue-no"
                      onClick={() => {
                        setIsClickDelete(false)
                        return false
                      }}
                      className="ml-3 cursor-pointer text-[14px] text-secondaryIndigo underline hover:opacity-[0.8]"
                    >
                      Cancel
                    </span>
                  </>
                )}
                {!isClickDelete && canEditCatalog && isManagement && (
                  <>
                    <input
                      type="checkbox"
                      data-testid="input-select-all"
                      onChange={() => {
                        setSelectAll(!selectAll)
                        if (!selectAll) {
                          setCheckedIds(documents.map((doc) => doc.id))
                        } else {
                          setCheckedIds([])
                        }
                      }}
                      checked={selectAll}
                    />
                    <div className="w-[12px] cursor-pointer">&nbsp;</div>
                    <label className="checkbox-label cursor-pointer">
                      Select all
                    </label>
                    {canDeleteFromCatalogue && (
                      <>
                        <span
                          data-testid="btn-delete-from-catalogue"
                          onClick={onClickDelete}
                          className="ml-3 cursor-pointer text-[14px] text-secondaryIndigo underline hover:opacity-[0.8] "
                        >
                          Delete from catalogue
                        </span>
                        <span className="ml-3 text-[14px]">or</span>
                      </>
                    )}
                    <span
                      data-testid="btn-change-business-unit"
                      onClick={onClickChangeBusinessUnit}
                      className="ml-2 cursor-pointer text-[14px] text-purple hover:text-black"
                    >
                      Change Business Unit
                    </span>
                  </>
                )}
              </>
            )}
          </div>
          <div className="flex h-[100%] flex-col pt-3 min-h-[600px]">
            {isSearching && <Spinner />}
            {!isSearching && documents.length === 0 && (
              <div className="flex flex-none">
                <div className="flex flex-none">
                  <span className="mr-4 text-[16px] font-bold">
                    No file(s) found.
                  </span>
                </div>
              </div>
            )}
            {!isSearching && documents.length > 0 && (
              <div className="mt-3 flex-1">
                <div className="mentem-scrollbar-2 max-h-[60vh] overflow-y-auto">
                  <table
                    id="THE_TABLE"
                    className="mentem-table mentem-table-highlight"
                  >
                    <thead>
                      <tr>
                        <th
                          className={`${colStyle('th', true)} w-[270px]`}
                          style={{ zIndex: 2 }}
                        >
                          <div className="flex flex-row items-center px-2">
                            <span className="grow">File name</span>
                            <img
                              className="cursor-pointer"
                              src={sortSvg}
                              alt="sort"
                              onClick={() => onClickSort(SortBy.Filename)}
                              data-tooltip-id="tooltip-filename"
                              data-testid="sort-filename"
                            />
                          </div>
                        </th>
                        <th
                          className={`${colStyle('th')} w-[200px]`}
                          style={{ zIndex: 2 }}
                        >
                          <div className="flex flex-row items-center px-2">
                            <span className="grow">Published by</span>
                            <img
                              className="cursor-pointer"
                              src={sort2Svg}
                              alt="sort"
                              onClick={() => onClickSort(SortBy.PublishedBy)}
                              data-tooltip-id="tooltip-published-by"
                              data-testid="sort-published-by"
                            />
                          </div>
                        </th>
                        <th
                          className={`${colStyle('th')} w-[150px]`}
                          style={{ zIndex: 2 }}
                        >
                          <div className="flex flex-row items-center px-2">
                            <span className="grow">Date/Time</span>
                            <img
                              className="cursor-pointer pl-2 pr-2"
                              src={sortSvg}
                              alt="sort"
                              onClick={() => onClickSort(SortBy.DateTime)}
                              data-tooltip-id="tooltip-date-time"
                              data-testid="sort-date-time"
                            />
                          </div>
                        </th>
                        <th
                          className={`${colStyle('th')} w-[160px]`}
                          style={{ zIndex: 2 }}
                        >
                          <div className="flex flex-row items-center px-2">
                            <span className="grow">Business unit</span>
                            <img
                              className="cursor-pointer pl-2 pr-2"
                              src={sort2Svg}
                              alt="sort"
                              onClick={() => onClickSort(SortBy.BusinessUnit)}
                              data-tooltip-id="tooltip-business-unit"
                              data-testid="sort-business-unit"
                            />
                          </div>
                        </th>
                        {(fileTypeFilter.audio || fileTypeFilter.videos) && (
                          <th
                            className={`${colStyle('th')} w-[120px]`}
                            style={{ zIndex: 2 }}
                          >
                            <div className="flex flex-row items-center px-2">
                              <span className="grow">Duration</span>
                              <img
                                className="cursor-pointer pl-2 pr-2"
                                src={sortSvg}
                                alt="sort"
                                onClick={() => onClickSort(SortBy.Duration)}
                                data-tooltip-id="tooltip-duration"
                                data-testid="sort-duration"
                              />
                            </div>
                          </th>
                        )}
                        <th
                          className={`${colStyle('th')} w-[90px]`}
                          style={{ zIndex: 2 }}
                        >
                          View
                        </th>
                        <th
                          className={`${colStyle('th')} w-[90px]`}
                          style={{ zIndex: 2 }}
                        >
                          Notes
                        </th>
                        <th
                          className={`${colStyle('th')} w-[100px]`}
                          style={{ zIndex: 2 }}
                        >
                          Copy link
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDocuments.map((lxFile: LxFile, index: number) => {
                        return (
                          <tr data-testid="tr-document" key={index}>
                            <td
                              className={`${colStyle(
                                'td',
                                true,
                              )} mentem-title-column`}
                            >
                              <div className="flex flex-row">
                                {isManagement && (
                                  <input
                                    type="checkbox"
                                    className="self-center"
                                    data-id="checkbox"
                                    checked={checkedIds.includes(lxFile.id)}
                                    onChange={(e) =>
                                      onChangeCheckbox(
                                        e.target.checked,
                                        lxFile.id,
                                      )
                                    }
                                  />
                                )}
                                <div className="pl-2">
                                  <div className="mentem-title-box flex items-center gap-1">
                                    {lxFile.name}
                                    {lxFile.hasEmbedding === false &&
                                      !fileTypeFilter.other && (
                                        <button
                                          className="text-red-500 text-xs font-bold shrink-0 hover:text-red-700"
                                          title="No embedding — click to generate"
                                          onClick={async (e) => {
                                            e.stopPropagation()
                                            try {
                                              await generateLxFileEmbedding(
                                                lxFile.id,
                                              )
                                              setDocuments((prev) =>
                                                prev.map((d) =>
                                                  d.id === lxFile.id
                                                    ? {
                                                        ...d,
                                                        hasEmbedding: true,
                                                      }
                                                    : d,
                                                ),
                                              )
                                            } catch {
                                              ToastUtil.error(
                                                'Failed to generate embedding',
                                              )
                                            }
                                          }}
                                        >
                                          ✕
                                        </button>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className={colStyle('td')}>
                              <div className="flex items-center justify-center">
                                {lxFile.publishedUserName || 'Unknown'}
                              </div>
                            </td>
                            <td className={colStyle('td')}>
                              <div className="flex items-center justify-center">
                                {getDateText(lxFile.createdAt)}
                              </div>
                              <div className="flex items-center justify-center">
                                {getTimeText(lxFile.createdAt)}
                              </div>
                            </td>
                            <td className={colStyle('td')}>
                              <div className="flex items-center justify-center">
                                {lxFile.business_unit || null}
                              </div>
                            </td>
                            {(fileTypeFilter.audio ||
                              fileTypeFilter.videos) && (
                              <td className={colStyle('td')}>
                                <div className="flex items-center justify-center">
                                  {lxFile.duration != null
                                    ? formatDuration(lxFile.duration)
                                    : '—'}
                                </div>
                              </td>
                            )}
                            <td className={colStyle('td')}>
                              <div className="flex items-center justify-center">
                                <a
                                  href={lxFile.fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img
                                    src={getExtensionIcon(lxFile.name)}
                                    alt={lxFile.name}
                                  />
                                </a>
                              </div>
                            </td>
                            <td className={colStyle('td')}>
                              <div className="flex items-center justify-center">
                                <button
                                  onClick={() => {
                                    if (canEditCatalog) {
                                      if (checkedIds.length === 0) {
                                        setEditingDocs([lxFile])
                                      } else {
                                        setEditingDocs(
                                          documents.filter((doc) =>
                                            checkedIds.includes(doc.id),
                                          ),
                                        )
                                      }
                                    } else {
                                      ToastUtil.warning(
                                        'You do not have permission to edit',
                                      )
                                    }
                                  }}
                                >
                                  <img
                                    data-tooltip-id={`tt-comments-${lxFile.id}`}
                                    data-tooltip-content={
                                      lxFile.comments || 'No notes'
                                    }
                                    src={
                                      lxFile.comments ? hasNotesSvg : noNotesSvg
                                    }
                                    alt={lxFile.comments}
                                  />{' '}
                                </button>
                              </div>
                            </td>
                            <td className={colStyle('td')}>
                              <div className="flex items-center justify-center">
                                <img
                                  className="cursor-pointer"
                                  src={copySvg}
                                  alt="copy"
                                  data-tooltip-id={`tooltip-media-copy-${lxFile.id}`}
                                  onClick={() => {
                                    onClickCopy(lxFile)
                                  }}
                                />
                                <Tooltip
                                  id={`tooltip-media-copy-${lxFile.id}`}
                                  className="mentem-tooltip"
                                  style={tooltipStyle}
                                  place="top"
                                  noArrow={true}
                                >
                                  Copy link
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                <Tooltip
                  id="tooltip-filename"
                  className="mentem-tooltip"
                  style={getTooptipStyle(SortBy.Filename)}
                  place="top"
                  noArrow={true}
                >
                  {getSortingText(SortBy.Filename)}
                </Tooltip>
                <Tooltip
                  id="tooltip-published-by"
                  className="mentem-tooltip"
                  style={getTooptipStyle(SortBy.PublishedBy)}
                  place="top"
                  noArrow={true}
                >
                  {getSortingText(SortBy.PublishedBy)}
                </Tooltip>
                <Tooltip
                  id="tooltip-date-time"
                  className="mentem-tooltip"
                  style={getTooptipStyle(SortBy.DateTime)}
                  place="top"
                  noArrow={true}
                >
                  {getSortingText(SortBy.DateTime)}
                </Tooltip>
                <Tooltip
                  id="tooltip-business-unit"
                  className="mentem-tooltip"
                  style={getTooptipStyle(SortBy.BusinessUnit)}
                  place="top"
                  noArrow={true}
                >
                  {getSortingText(SortBy.BusinessUnit)}
                </Tooltip>
                <Tooltip
                  id="tooltip-duration"
                  className="mentem-tooltip"
                  style={getTooptipStyle(SortBy.Duration)}
                  place="top"
                  noArrow={true}
                >
                  {getSortingText(SortBy.Duration)}
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

function MediaPage() {
  const [sideBarMinimized, setSideBarMinimized] = useState<boolean>(false)
  const [activeSection, setActiveSection] = useState<Section>(
    Section.ImagesSection,
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [fileTypeFilter, setFileTypeFilter] = useState({
    documents: false,
    presentations: false,
    spreadsheets: false,
    audio: false,
    videos: false,
    other: false,
  })
  const [description, setDescription] = useState('')
  const isActiveSection = (targetSection: Section) => {
    return activeSection === targetSection
      ? StyleUtil.buttonPrimaryUnsw
      : StyleUtil.buttonBlackWhite
  }

  const { user, getRole } = useCompositeAuth()
  const role = getRole()
  const isSuperAdmin = role === Role.SuperAdmin
  const canUploadImagesInCatalogue =
    role === Role.SuperAdmin ||
    role === Role.Admin ||
    role === Role.Author ||
    role === Role.Designer ||
    role === Role.Contractor
  const canManageImagesInCatalogue =
    role === Role.SuperAdmin || role === Role.Admin || role === Role.Designer
  const canAccessManageSection = isSuperAdmin
  const { uploadLxFile } = useApi()
  const refInput = useRef<HTMLInputElement | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedLxFile[]>([])
  const [filesToUpload, setFilesToUpload] = useState<LxFileContainer[]>([])
  const refSelectedFileUrls = useRef<string[]>([])

  const [uploadProgress, setUploadProgress] = useState<any>({})

  const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024 // 5GB

  const addFiles = (newFiles: File[]) => {
    const oversized = newFiles.filter((file) => file.size > MAX_FILE_SIZE)
    if (oversized.length > 0) {
      ToastUtil.error('Upload failed: file size too large (over 5GB)')
      return
    }
    const tmpFiles = newFiles.map((file) => {
      return {
        objectURL: URL.createObjectURL(file),
        file: file,
        filenameOverride: file.name,
      }
    })
    setFilesToUpload(filesToUpload.concat(tmpFiles))
    refSelectedFileUrls.current = refSelectedFileUrls.current.concat(
      tmpFiles.map((file) => file.objectURL),
    )
  }

  const hasFiles = (e: React.DragEvent<HTMLDivElement>) => {
    const types = e.dataTransfer.types
    return types.indexOf('Files') > -1
  }
  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const newFiles: File[] = []
    for (const file of e.dataTransfer.files) {
      if (!file.type.startsWith('image/')) {
        newFiles.push(file)
      } else {
        ToastUtil.errorHtml(FileErrorMessage)
      }
    }
    addFiles(newFiles)
  }
  const onDragEnterHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!hasFiles(e)) {
      return
    }
  }

  const onDragLeaveHandler = (e: React.DragEvent<HTMLDivElement>) => {}

  const onDragOverHandler = (e: React.DragEvent<HTMLDivElement>) => {
    if (hasFiles(e)) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  const onInputFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files || []
    console.log(files)
    const newFiles: File[] = []
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        newFiles.push(file)
      } else {
        ToastUtil.errorHtml(FileErrorMessage)
      }
    }
    addFiles(newFiles)
    refInput.current!.value = ''
  }

  const onDeletePreview = (id: string) => {
    const newFiles = filesToUpload.filter((file) => {
      return file.objectURL !== id
    })
    setFilesToUpload(newFiles)
  }

  const onCopy = (url: string) => {
    url = url.indexOf('%') > -1 ? url : encodeURI(url)
    navigator.clipboard.writeText(url)
    ToastUtil.success('URL link copied to clipboard')
  }

  const onSubmit = async (businessUnit: string) => {
    if (filesToUpload.length === 0) {
      ToastUtil.error('Please select files')
      return
    }

    const username = user?.username || ''

    setIsLoading(true)
    const uploadedFiles: UploadedLxFile[] = []
    const failedFiles: LxFileContainer[] = []
    for (const theFile of filesToUpload) {
      let formData = new FormData()
      formData.append('file', theFile.file)
      formData.append('userId', user?.id || '')
      formData.append('filenameOverride', theFile.filenameOverride)
      formData.append('description', description)
      formData.append('username', username)
      formData.append('businessUnit', businessUnit)

      try {
        const data = await uploadLxFile(
          formData,
          (percentCompleted: number) => {
            setUploadProgress((progressInfo: any) => ({
              ...progressInfo,
              [theFile.objectURL]: {
                progress: percentCompleted,
                status: 'uploading',
              },
            }))
          },
        )
        setUploadProgress((progressInfo: any) => ({
          ...progressInfo,
          [theFile.objectURL]: {
            ...progressInfo[theFile.objectURL],
            status: 'success',
          },
        }))
        uploadedFiles.push({
          url: data.fileUrl,
          filename: theFile.filenameOverride,
        })
      } catch (err: any) {
        setUploadProgress((progressInfo: any) => ({
          ...progressInfo,
          [theFile.objectURL]: {
            ...progressInfo[theFile.objectURL],
            progress: 100,
            status: 'failed',
          },
        }))
        console.error(err)
        if (err.response?.data?.message) {
          ToastUtil.error(err.response.data.message)
        } else if (err.message) {
          ToastUtil.error(err.message)
        } else {
          ToastUtil.error(`Upload failed<br/>${err}`)
        }
        failedFiles.push(theFile)
      }
    }
    setIsLoading(false)
    setUploadedFiles(uploadedFiles)
    setFilesToUpload(failedFiles)
    refSelectedFileUrls.current = []

    uploadedFiles.length &&
      ToastUtil.success(`Uploaded ${uploadedFiles.length} files successfully`)

    if (!failedFiles.length) {
      setDescription('')
    }
  }

  return (
    <SidebarLayout>
      <div className="flex">
        {[
          Section.Search,
          Section.ManageSection,
          Section.ImagesSection,
        ].includes(activeSection) && (
          <div style={{}}>
            <div
              id="media-sidebar"
              className={sideBarMinimized ? 'minimized' : ''}
              style={{
                width: sideBarMinimized ? '80px' : '136px',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  textAlign: 'center',
                  maxWidth: sideBarMinimized ? '70px' : '130px',
                }}
              >
                <button
                  className="w-full"
                  onClick={() => {
                    setActiveSection(Section.ImagesSection)
                    setFileTypeFilter({
                      documents: false,
                      presentations: false,
                      spreadsheets: false,
                      audio: false,
                      videos: false,
                      other: false,
                    })
                  }}
                >
                  <div
                    className={`file-catalogue-tab ${
                      activeSection === Section.ImagesSection ? 'active' : ''
                    } ${sideBarMinimized ? 'minimized' : ''}`}
                  >
                    <FcImagesSvg
                      className="mx-auto w-[27px]"
                      style={{
                        color:
                          activeSection === Section.ImagesSection
                            ? '#FFDC00'
                            : 'black',
                      }}
                    />
                    {sideBarMinimized ? '' : <p>Images</p>}
                  </div>
                </button>
                <button
                  className="w-full"
                  onClick={() => {
                    if (
                      ![Section.Search, Section.ManageSection].includes(
                        activeSection,
                      )
                    ) {
                      setActiveSection(Section.Search)
                    }
                    setFileTypeFilter({
                      documents: true,
                      presentations: false,
                      spreadsheets: false,
                      audio: false,
                      videos: false,
                      other: false,
                    })
                  }}
                >
                  <div
                    className={`file-catalogue-tab ${
                      fileTypeFilter.documents ? 'active' : ''
                    } ${sideBarMinimized ? 'minimized' : ''}`}
                  >
                    <FcDocumentsSvg
                      className="mx-auto w-[27px]"
                      style={{
                        color: fileTypeFilter.documents ? '#FFDC00' : 'black',
                      }}
                    />
                    {sideBarMinimized ? '' : <p>Documents</p>}
                  </div>
                </button>
                <button
                  className="w-full"
                  onClick={() => {
                    if (
                      ![Section.Search, Section.ManageSection].includes(
                        activeSection,
                      )
                    ) {
                      setActiveSection(Section.Search)
                    }
                    setFileTypeFilter({
                      documents: false,
                      presentations: true,
                      spreadsheets: false,
                      audio: false,
                      videos: false,
                      other: false,
                    })
                  }}
                >
                  <div
                    className={`file-catalogue-tab ${
                      fileTypeFilter.presentations ? 'active' : ''
                    } ${sideBarMinimized ? 'minimized' : ''}`}
                  >
                    <FcPresentationsSvg
                      className="mx-auto w-[27px]"
                      style={{
                        color: fileTypeFilter.presentations
                          ? '#FFDC00'
                          : 'black',
                      }}
                    />
                    {sideBarMinimized ? '' : <p>Presentations</p>}
                  </div>
                </button>
                <button
                  className="w-full"
                  onClick={() => {
                    if (
                      ![Section.Search, Section.ManageSection].includes(
                        activeSection,
                      )
                    ) {
                      setActiveSection(Section.Search)
                    }
                    setFileTypeFilter({
                      documents: false,
                      presentations: false,
                      spreadsheets: true,
                      audio: false,
                      videos: false,
                      other: false,
                    })
                  }}
                >
                  <div
                    className={`file-catalogue-tab ${
                      fileTypeFilter.spreadsheets ? 'active' : ''
                    } ${sideBarMinimized ? 'minimized' : ''}`}
                  >
                    <FcSpreadsheetsSvg
                      className="mx-auto w-[27px]"
                      style={{
                        color: fileTypeFilter.spreadsheets
                          ? '#FFDC00'
                          : 'black',
                      }}
                    />
                    {sideBarMinimized ? '' : <p>Spreadsheets</p>}
                  </div>
                </button>
                <button
                  className="w-full"
                  onClick={() => {
                    if (
                      ![Section.Search, Section.ManageSection].includes(
                        activeSection,
                      )
                    ) {
                      setActiveSection(Section.Search)
                    }
                    setFileTypeFilter({
                      documents: false,
                      presentations: false,
                      spreadsheets: false,
                      audio: true,
                      videos: false,
                      other: false,
                    })
                  }}
                >
                  <div
                    className={`file-catalogue-tab ${
                      fileTypeFilter.audio ? 'active' : ''
                    } ${sideBarMinimized ? 'minimized' : ''}`}
                  >
                    <FcAudioSvg
                      className="mx-auto w-[27px]"
                      style={{
                        color: fileTypeFilter.audio ? '#FFDC00' : 'black',
                      }}
                    />
                    {sideBarMinimized ? '' : <p>Audio</p>}
                  </div>
                </button>
                <button
                  className="w-full"
                  onClick={() => {
                    if (
                      ![Section.Search, Section.ManageSection].includes(
                        activeSection,
                      )
                    ) {
                      setActiveSection(Section.Search)
                    }
                    setFileTypeFilter({
                      documents: false,
                      presentations: false,
                      spreadsheets: false,
                      audio: false,
                      videos: true,
                      other: false,
                    })
                  }}
                >
                  <div
                    className={`file-catalogue-tab ${
                      fileTypeFilter.videos ? 'active' : ''
                    } ${sideBarMinimized ? 'minimized' : ''}`}
                  >
                    <FcVideoSvg
                      className="mx-auto w-[27px]"
                      style={{
                        color: fileTypeFilter.videos ? '#FFDC00' : 'black',
                      }}
                    />
                    {sideBarMinimized ? '' : <p>Videos</p>}
                  </div>
                </button>
                <button
                  className="w-full"
                  onClick={() => {
                    if (
                      ![Section.Search, Section.ManageSection].includes(
                        activeSection,
                      )
                    ) {
                      setActiveSection(Section.Search)
                    }
                    setFileTypeFilter({
                      documents: false,
                      presentations: false,
                      spreadsheets: false,
                      audio: false,
                      videos: false,
                      other: true,
                    })
                  }}
                >
                  <div
                    className={`file-catalogue-tab ${
                      fileTypeFilter.other ? 'active' : ''
                    } ${sideBarMinimized ? 'minimized' : ''}`}
                  >
                    <FcOtherSvg
                      className="mx-auto w-[27px]"
                      style={{
                        color: fileTypeFilter.other ? '#FFDC00' : 'black',
                      }}
                    />
                    {sideBarMinimized ? '' : <p>Others</p>}
                  </div>
                </button>
              </div>
              <div
                className={
                  sideBarMinimized
                    ? 'sidebar-button-left'
                    : 'sidebar-button-right'
                }
                style={{
                  left: sideBarMinimized ? 74 : 122,
                  position: 'relative',
                  backgroundColor: 'var(--tertiary-teal)',
                }}
              >
                <button onClick={() => setSideBarMinimized(!sideBarMinimized)}>
                  <img
                    src={sideBarMinimized ? menuRight : menuLeft}
                    alt="menu-button"
                  />
                </button>
              </div>
            </div>
          </div>
        )}
        <div
          className={StyleUtil.containerNoScroll}
          style={{
            overflowY: 'auto',
          }}
        >
          <p className={StyleUtil.headline}>
            {activeSection === Section.ImagesSection &&
            canManageImagesInCatalogue
              ? 'Media images: Search, upload, or manage images'
              : activeSection === Section.ImagesSection &&
                canUploadImagesInCatalogue
              ? 'Media images: Search or upload images'
              : activeSection === Section.ImagesSection
              ? 'Media: Search images to use in topics'
              : activeSection === Section.Search && fileTypeFilter.documents
              ? 'Media: Search or upload documents'
              : activeSection === Section.Search && fileTypeFilter.presentations
              ? 'Media: Search or upload presentations'
              : activeSection === Section.Search && fileTypeFilter.spreadsheets
              ? 'Media: Search or upload spreadsheets'
              : activeSection === Section.Search && fileTypeFilter.audio
              ? 'Media: Search or upload audio'
              : activeSection === Section.Search && fileTypeFilter.videos
              ? 'Media: Search or upload videos'
              : activeSection === Section.Search && fileTypeFilter.other
              ? 'Media: Search or upload other files'
              : activeSection === Section.Upload
              ? 'Media: Upload files'
              : activeSection === Section.ManageSection &&
                fileTypeFilter.documents
              ? 'Media: Search or upload documents'
              : activeSection === Section.ManageSection &&
                fileTypeFilter.presentations
              ? 'Media: Search or upload presentations'
              : activeSection === Section.ManageSection &&
                fileTypeFilter.spreadsheets
              ? 'Media: Search or upload spreadsheets'
              : activeSection === Section.ManageSection && fileTypeFilter.audio
              ? 'Media: Search or upload audio'
              : activeSection === Section.ManageSection && fileTypeFilter.videos
              ? 'Media: Search or upload videos'
              : activeSection === Section.ManageSection && fileTypeFilter.other
              ? 'Media: Search or upload other files'
              : 'Media catalogue / '}
          </p>
          <div className="flex h-[100%] flex-col pb-10 pt-8">
            {activeSection !== Section.ImagesSection && (
              <div className="mt-4 flex justify-center gap-9">
                <button
                  data-testid="btn-search-section"
                  className={isActiveSection(Section.Search)}
                  onClick={() => setActiveSection(Section.Search)}
                >
                  <span>
                    {fileTypeFilter.documents
                      ? 'Documents'
                      : fileTypeFilter.presentations
                      ? 'Presentations'
                      : fileTypeFilter.spreadsheets
                      ? 'Spreadsheets'
                      : fileTypeFilter.audio
                      ? 'Audio'
                      : fileTypeFilter.videos
                      ? 'Videos'
                      : fileTypeFilter.other
                      ? 'Other files'
                      : 'Search'}
                  </span>
                </button>
                <button
                  data-testid="btn-upload-section"
                  className={isActiveSection(Section.Upload)}
                  onClick={() => setActiveSection(Section.Upload)}
                >
                  <span>Upload</span>
                </button>
                {canAccessManageSection && (
                  <button
                    data-testid="btn-manage-section"
                    className={isActiveSection(Section.ManageSection)}
                    onClick={() => setActiveSection(Section.ManageSection)}
                  >
                    <span>Manage</span>
                  </button>
                )}
              </div>
            )}
            <main className="mt-4 w-full">
              {activeSection === Section.Search && (
                <SearchSection
                  fileTypeFilter={fileTypeFilter}
                  setFileTypeFilter={setFileTypeFilter}
                  isManagement={false}
                />
              )}
              {activeSection === Section.Upload && (
                <>
                  <div
                    className="flex justify-center pt-3"
                    data-testid="upload-description-container"
                  >
                    <label
                      className="w-full max-w-[488px]"
                      data-testid="label-description"
                    >
                      <input
                        data-testid="input-description"
                        id="input-description"
                        type="text"
                        className={StyleUtil.inputSearch}
                        style={{
                          boxShadow: 'none',
                          borderRadius: 0,
                          paddingLeft: 12,
                        }}
                        placeholder="Add file note(s) here if required"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </label>
                  </div>
                  <article
                    data-testid="upload-modal"
                    aria-label="File Upload Modal"
                    className="relative mx-auto mb-0 mt-6 flex flex-col bg-white"
                    style={{
                      maxWidth: 560,
                      paddingTop: 24,
                      paddingLeft: 36,
                      paddingRight: 36,
                      paddingBottom: 36,
                      boxShadow:
                        '0px 570px 160px 0px rgba(0, 0, 0, 0.00), 0px 365px 146px 0px rgba(0, 0, 0, 0.01), 0px 205px 123px 0px rgba(0, 0, 0, 0.05), 0px 91px 91px 0px rgba(0, 0, 0, 0.09), 0px 23px 50px 0px rgba(0, 0, 0, 0.10)',
                    }}
                    onDrop={onDropHandler}
                    onDragOver={onDragOverHandler}
                    onDragLeave={onDragLeaveHandler}
                    onDragEnter={onDragEnterHandler}
                  >
                    <section
                      className="mx-auto flex h-full w-full flex-col overflow-hidden"
                      data-testid="upload-section"
                    >
                      {uploadedFiles.length > 0 ? (
                        <header
                          className="border-1 flex h-[270px] flex-col items-center justify-center border border-gray-400"
                          data-testid="upload-dropzone"
                        >
                          <svg
                            width="522"
                            height="272"
                            viewBox="0 0 522 272"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="0.5"
                              y="0.5"
                              width="521"
                              height="271"
                              stroke="#C8C8C8"
                            />
                            <path
                              d="M314.75 92C314.75 102.631 311.598 113.023 305.692 121.862C299.785 130.701 291.391 137.59 281.569 141.659C271.748 145.727 260.94 146.791 250.514 144.717C240.087 142.643 230.51 137.524 222.993 130.007C215.476 122.49 210.357 112.913 208.283 102.486C206.209 92.0596 207.273 81.2523 211.341 71.4308C215.41 61.6092 222.299 53.2146 231.138 47.3085C239.977 41.4024 250.369 38.25 261 38.25M299.785 53.215L257.723 110.47L230.58 87.7475"
                              stroke="#1AC987"
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M168.607 234.1C165.147 234.1 162.707 232.2 162.707 228.7V220H165.527V228.44C165.527 230.62 167.067 231.46 168.567 231.46C170.047 231.46 171.447 230.62 171.447 228.44V220H174.247V228.7C174.247 232.2 171.627 234.1 168.607 234.1ZM181.748 223.94C184.588 223.76 186.948 225.5 186.908 228.96C186.868 232.4 184.608 234.16 181.748 234.1C180.788 234.08 179.988 233.82 179.368 233.36V238.34H176.548V224H179.108L179.208 224.9C179.968 224.3 180.888 224 181.748 223.94ZM184.068 228.96C184.088 227.2 183.108 226.32 181.608 226.4C180.408 226.46 179.368 227.26 179.368 228.76V229.14C179.368 230.62 180.388 231.46 181.608 231.52C183.028 231.58 184.048 230.66 184.068 228.96ZM188.911 234V219.66H191.731V234H188.911ZM198.786 234.1C195.846 234.1 193.546 231.8 193.546 228.94C193.546 226.2 195.846 223.9 198.786 223.9C201.726 223.9 204.026 226.2 204.026 228.94C204.026 231.8 201.726 234.1 198.786 234.1ZM198.786 231.52C200.006 231.52 201.026 230.52 201.006 228.92C200.986 227.46 200.006 226.48 198.786 226.48C197.546 226.48 196.526 227.46 196.546 228.92C196.566 230.52 197.546 231.52 198.786 231.52ZM208.781 234.1C206.861 234.18 205.121 233.32 205.121 231.22C205.121 229.02 206.921 227.98 209.441 227.94C210.221 227.92 210.841 227.96 211.361 228.02V227.62C211.361 226.82 210.841 226.32 209.661 226.32C208.441 226.32 207.101 226.88 206.521 227.3L205.481 225.14C206.741 224.38 208.101 223.9 210.041 223.9C213.201 223.9 214.341 225.84 214.341 227.68V234H211.601L211.461 232.8C211.021 233.4 210.141 234.04 208.781 234.1ZM209.501 231.98C210.521 231.84 211.241 231.14 211.361 230.5V230.06C210.881 229.98 210.261 229.94 209.681 229.96C208.601 230 207.921 230.44 208.021 231.22C208.081 231.7 208.481 232.12 209.501 231.98ZM223.581 219.9H226.401V234H223.941L223.781 233.22C223.141 233.76 222.241 234.06 221.061 234.1C218.541 234.16 216.241 232.34 216.241 228.96C216.241 225.94 218.381 223.78 221.201 223.9C222.201 223.94 222.981 224.16 223.581 224.56V219.9ZM221.521 231.48C222.541 231.46 223.581 230.62 223.581 229.14V228.76C223.581 227.26 222.561 226.4 221.561 226.4C220.121 226.4 219.061 227.2 219.081 228.96C219.101 230.62 220.121 231.5 221.521 231.48ZM233.469 223.9C237.169 223.9 238.849 226.6 238.289 229.96L231.349 229.98C231.589 231.12 232.669 231.6 233.669 231.6C234.869 231.6 235.649 230.9 236.049 230.38L238.029 232.02C237.289 232.98 235.869 234.12 233.349 234.1C230.629 234.08 228.449 232.06 228.409 229.08C228.369 225.82 230.529 223.9 233.469 223.9ZM233.449 226.24C232.529 226.24 231.589 226.7 231.369 227.96H235.509C235.489 226.8 234.489 226.24 233.449 226.24ZM247.136 219.9H249.956V234H247.496L247.336 233.22C246.696 233.76 245.796 234.06 244.616 234.1C242.096 234.16 239.796 232.34 239.796 228.96C239.796 225.94 241.936 223.78 244.756 223.9C245.756 223.94 246.536 224.16 247.136 224.56V219.9ZM245.076 231.48C246.096 231.46 247.136 230.62 247.136 229.14V228.76C247.136 227.26 246.116 226.4 245.116 226.4C243.676 226.4 242.616 227.2 242.636 228.96C242.656 230.62 243.676 231.5 245.076 231.48ZM266.209 225.42L264.469 227.24C264.189 226.88 263.029 226.28 261.889 226.28C261.069 226.28 260.729 226.54 260.729 226.8C260.729 227.34 261.749 227.48 262.909 227.78C264.469 228.18 266.249 228.68 266.249 230.76C266.249 233.02 264.649 234.1 262.269 234.1C260.309 234.1 258.549 233.52 257.469 232.46L259.289 230.56C259.889 231.04 260.869 231.6 262.109 231.6C262.969 231.6 263.429 231.38 263.429 231.04C263.429 230.4 262.009 230.26 260.609 229.82C259.169 229.36 257.689 228.5 257.889 226.62C258.069 224.84 259.829 223.9 261.989 223.9C263.949 223.9 265.269 224.54 266.209 225.42ZM274.43 224H277.41V234H274.63L274.51 233.02C273.87 233.72 272.97 234.1 271.67 234.1C269.03 234.1 268.05 232.18 268.05 230.34V224H271.03V229.72C271.03 230.82 271.35 231.66 272.49 231.66C273.69 231.66 274.43 231.04 274.43 229.46V224ZM284.074 234.1C281.134 234.1 279.054 232.06 279.014 228.96C278.974 225.86 281.194 223.9 284.134 223.9C285.834 223.92 287.214 224.6 288.134 225.74L286.194 227.48C285.574 226.7 284.954 226.38 284.134 226.38C283.034 226.38 281.914 227.2 281.934 228.96C281.954 230.6 282.894 231.52 284.114 231.52C285.194 231.52 285.794 231 286.294 230.32L288.334 232.06C287.514 233.22 286.074 234.1 284.074 234.1ZM294.309 223.9C298.009 223.9 299.689 226.6 299.129 229.96L292.189 229.98C292.429 231.12 293.509 231.6 294.509 231.6C295.709 231.6 296.489 230.9 296.889 230.38L298.869 232.02C298.129 232.98 296.709 234.12 294.189 234.1C291.469 234.08 289.289 232.06 289.249 229.08C289.209 225.82 291.369 223.9 294.309 223.9ZM294.289 226.24C293.369 226.24 292.429 226.7 292.209 227.96H296.349C296.329 226.8 295.329 226.24 294.289 226.24ZM309.276 225.42L307.536 227.24C307.256 226.88 306.096 226.28 304.956 226.28C304.136 226.28 303.796 226.54 303.796 226.8C303.796 227.34 304.816 227.48 305.976 227.78C307.536 228.18 309.316 228.68 309.316 230.76C309.316 233.02 307.716 234.1 305.336 234.1C303.376 234.1 301.616 233.52 300.536 232.46L302.356 230.56C302.956 231.04 303.936 231.6 305.176 231.6C306.036 231.6 306.496 231.38 306.496 231.04C306.496 230.4 305.076 230.26 303.676 229.82C302.236 229.36 300.756 228.5 300.956 226.62C301.136 224.84 302.896 223.9 305.056 223.9C307.016 223.9 308.336 224.54 309.276 225.42ZM319.256 225.42L317.516 227.24C317.236 226.88 316.076 226.28 314.936 226.28C314.116 226.28 313.776 226.54 313.776 226.8C313.776 227.34 314.796 227.48 315.956 227.78C317.516 228.18 319.296 228.68 319.296 230.76C319.296 233.02 317.696 234.1 315.316 234.1C313.356 234.1 311.596 233.52 310.516 232.46L312.336 230.56C312.936 231.04 313.916 231.6 315.156 231.6C316.016 231.6 316.476 231.38 316.476 231.04C316.476 230.4 315.056 230.26 313.656 229.82C312.216 229.36 310.736 228.5 310.936 226.62C311.116 224.84 312.876 223.9 315.036 223.9C316.996 223.9 318.316 224.54 319.256 225.42ZM325.756 222.14C324.856 222.14 324.456 222.66 324.456 223.66V224.1L327.036 224.12V226.36H324.456V234H321.636V226.36H319.996V224.18C321.296 224.18 321.636 223.66 321.636 222.96C321.636 220.86 323.356 219.66 324.956 219.66H327.076V222.14H325.756ZM334.859 224H337.839V234H335.059L334.939 233.02C334.299 233.72 333.399 234.1 332.099 234.1C329.459 234.1 328.479 232.18 328.479 230.34V224H331.459V229.72C331.459 230.82 331.779 231.66 332.919 231.66C334.119 231.66 334.859 231.04 334.859 229.46V224ZM340.435 234V219.66H343.255V234H340.435ZM345.864 234V219.66H348.684V234H345.864ZM357.814 224H360.694L356.734 234L355.094 238.34H352.214L354.014 233.58L350.194 224H353.174L355.414 230.3H355.474L357.814 224Z"
                              fill="#050505"
                            />
                          </svg>
                        </header>
                      ) : (
                        <header
                          className="border-1 flex h-[270px] flex-col items-center justify-center border border-gray-400"
                          data-testid="upload-dropzone"
                        >
                          <p
                            className="flex flex-wrap justify-center"
                            data-testid="upload-instruction-text"
                            style={{
                              fontWeight: 700,
                              fontSize: 20,
                            }}
                          >
                            <span>Drag file(s) here to upload</span>
                          </p>
                          <input
                            data-testid="hidden-input"
                            id="hidden-input"
                            title="hidden-input"
                            type="file"
                            multiple={true}
                            className="hidden"
                            ref={refInput}
                            onChange={onInputFiles}
                          />
                          <button
                            data-testid="btn-select"
                            type="button"
                            id="btn-select"
                            onClick={() => refInput.current?.click()}
                            className={`${StyleUtil.buttonPrimaryUnsw} mb-0 mt-[60px]`}
                          >
                            <span className="pl-2 pr-2">Select files</span>
                          </button>
                        </header>
                      )}

                      <div className="pt-5" data-testid="preview-label">
                        {/*  Preview*/}
                      </div>

                      <ul
                        id="gallery"
                        className="flex flex-1 flex-wrap"
                        data-testid="upload-gallery"
                      >
                        {filesToUpload.map((file, index) => {
                          const fileUploadProgress =
                            uploadProgress[file.objectURL]
                          const progressBarColor = fileUploadProgress
                            ? fileUploadProgress.status === 'failed'
                              ? 'red'
                              : 'blue'
                            : 'transparent'
                          return (
                            <li
                              id={file.objectURL}
                              data-testid={`upload-file-item-${index}`}
                              className="block w-full p-1 pt-[36px]"
                              key={file.objectURL}
                            >
                              <FilePreview
                                key={index}
                                fileObject={file}
                                onDelete={onDeletePreview}
                                onNameChange={(id, newName) => {
                                  const newFiles = filesToUpload.map((file) => {
                                    if (file.objectURL === id) {
                                      file.filenameOverride = newName
                                    }
                                    return file
                                  })
                                  setFilesToUpload(newFiles)
                                }}
                              />
                              <progress
                                data-testid={`upload-progress-${index}`}
                                value={fileUploadProgress?.progress || 0}
                                max="100"
                                style={{ color: progressBarColor }}
                              ></progress>
                              <span
                                data-testid={`upload-progress-text-${index}`}
                              >
                                {fileUploadProgress?.progress || 0}%
                              </span>
                              {fileUploadProgress &&
                                fileUploadProgress.status === 'failed' && (
                                  <span
                                    style={{ color: 'red' }}
                                    data-testid={`upload-error-${index}`}
                                  >
                                    Upload failed!
                                  </span>
                                )}
                            </li>
                          )
                        })}
                      </ul>
                    </section>
                    {isLoading && <Spinner data-testid="upload-spinner" />}
                    {uploadedFiles.length > 0 && (
                      <div className="" data-testid="uploaded-files-container">
                        <div className="m-0 p-0 font-bold mb-1">File link</div>
                        <ul
                          className="w-full mb-[-40px]"
                          data-testid="uploaded-files-list"
                        >
                          {uploadedFiles &&
                            uploadedFiles.map((upFile, index) => {
                              return (
                                <li
                                  key={index}
                                  className={index === 0 ? '' : 'mt-[32px]'}
                                  data-testid={`uploaded-file-${index}`}
                                >
                                  <div
                                    className="mb-[32px] flex flex-row items-center justify-between font-roboto"
                                    data-testid={`uploaded-file-info-${index}`}
                                  >
                                    <img
                                      alt={upFile.filename}
                                      className="w-[24px] shrink-0"
                                      src={getExtensionIcon(upFile.filename)}
                                      title={upFile.filename}
                                      data-testid={`uploaded-filename-${index}`}
                                    />
                                    <a
                                      href={upFile.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      data-testid={`uploaded-url-link-${index}`}
                                      className="whitespace-nowrap"
                                      style={{
                                        fontFamily: 'Arial',
                                        fontSize: 12,
                                        fontWeight: 400,
                                        color: '#347FF0',
                                        textDecorationLine: 'underline',
                                        fontStyle: 'italic',
                                      }}
                                      title={
                                        upFile.url.length > 66
                                          ? upFile.url
                                          : undefined
                                      }
                                    >
                                      {upFile.url.length > 66
                                        ? `${upFile.url.slice(0, 66)}…`
                                        : upFile.url}
                                    </a>
                                    <div className="-mr-2 h-4 align-super">
                                      <ButtonCopy
                                        url={upFile.url}
                                        onClick={onCopy}
                                      />
                                    </div>
                                  </div>
                                </li>
                              )
                            })}
                        </ul>
                      </div>
                    )}
                    <footer
                      className="m-0 mt-[36px] flex justify-between gap-3 p-0"
                      data-testid="upload-footer"
                    >
                      {uploadedFiles.length > 0 ? (
                        <div className="flex justify-between w-full">
                          <button
                            type="button"
                            onClick={() => setUploadedFiles([])}
                            className={StyleUtil.buttonPrimaryUnsw + ' !w-64'}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <span className="pl-2 pr-2">Upload more</span>
                            <RightArrowSvg />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setUploadedFiles([])
                              setActiveSection(Section.Search)
                            }}
                            className={StyleUtil.buttonWhiteBlack}
                          >
                            <span className="pl-2 pr-2">Done</span>
                          </button>
                        </div>
                      ) : (
                        businessUnitOptions.map(
                          ({ value, label, uploadStyle }) => (
                            <button
                              key={value}
                              data-testid={`btn-submit-${value}`}
                              type="button"
                              onClick={() => onSubmit(value)}
                              disabled={isLoading}
                              className={`${uploadStyle} !w-[142px] !h-[54px] !p-0 whitespace-normal`}
                            >
                              <span className="pl-2 pr-2">
                                Upload
                                <br />({label})
                              </span>
                            </button>
                          ),
                        )
                      )}
                    </footer>
                  </article>
                </>
              )}
              {canAccessManageSection &&
                activeSection === Section.ManageSection && (
                  <SearchSection
                    fileTypeFilter={fileTypeFilter}
                    setFileTypeFilter={setFileTypeFilter}
                    isManagement={true}
                  />
                )}

              {activeSection === Section.ImagesSection && <ImagesSection />}
            </main>
          </div>
        </div>
      </div>
    </SidebarLayout>
  )
}

export default MediaPage
